import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type Profile, type ForoTema, getTodasGuias } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { PrivateLayout } from "../components/PrivateLayout";
import { Reveal } from "../components/Reveal";
import { getCategory } from "../lib/categories";
import { relTime } from "../lib/time";

const TOPIC_LINKS = [
  { emoji: "😮‍💨", label: "Ansiedad",    color: "#F4E7DC" },
  { emoji: "💙",   label: "Depresión",   color: "#EAF0F4" },
  { emoji: "🌸",   label: "Autoestima",  color: "#F5EEDC" },
  { emoji: "❤️",   label: "Relaciones",  color: "#F4E7DC" },
  { emoji: "🌊",   label: "Estrés",      color: "#F1EAF3" },
  { emoji: "🌱",   label: "Crecimiento", color: "#E7EEEC" },
];

type PlanTier = "libre" | "plus" | "pro" | "max";
const PLAN_BADGE: Record<PlanTier, { label: string; bg: string; color: string }> = {
  libre: { label: "Libre", bg: "#E9F0E6", color: "#2F5742" },
  plus:  { label: "Plus",  bg: "#C49A8A", color: "#fff" },
  pro:   { label: "Pro",   bg: "#2D2D2D", color: "#fff" },
  max:   { label: "Max ✦", bg: "#D4B464", color: "#1A1208" },
};
// La landing ("/") redirige a /dashboard si ya hay sesión (ver PublicRoute
// en App.tsx) — "Mejorar plan" no puede mandar a /#membresia porque nunca
// se vería. En su lugar va directo al checkout del siguiente tier.
const NEXT_TIER: Record<PlanTier, Exclude<PlanTier, "libre"> | null> = {
  libre: "plus", plus: "pro", pro: "max", max: null,
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { token, user, clearSession, updateUser } = useAuth();
  const [profile,  setProfile]  = useState<Profile | null | undefined>(undefined);
  const [loading,  setLoading]  = useState(true);
  const [totalRes,   setTotalRes]   = useState<number | null>(null);
  const [totalTemas, setTotalTemas] = useState<number | null>(null);
  const [temas,      setTemas]      = useState<ForoTema[]>([]);
  const [planTier, setPlanTier]     = useState<PlanTier | null>(null);
  const [stats, setStats] = useState<{ ejercicios_completados: number; aportes_comunidad: number } | null>(null);

  useEffect(() => {
    if (!token) return;
    api.me(token).then((result) => {
      if (!result.ok) {
        clearSession();
        navigate("/login", { replace: true });
        return;
      }
      setProfile(result.data.profile);
      updateUser(result.data.user);
      setLoading(false);
    });
  }, [token]);

  useEffect(() => {
    getTodasGuias().then((r) => {
      if (r.ok) setTotalRes(r.data.total);
    }).catch(() => {});
    api.getForoTemas().then((r) => {
      if (r.ok) {
        setTotalTemas(r.data.total);
        setTemas(r.data.temas);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) return;
    api.getSubscripcion(token).then((r) => {
      if (r.ok) setPlanTier(r.data.plan_tier);
    }).catch(() => {});
    api.getUsuarioStats(token).then((r) => {
      if (r.ok) setStats(r.data);
    }).catch(() => {});
  }, [token]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
        <span className="spinner spinner--dark" />
      </div>
    );
  }

  const firstName = user?.nombre.split(" ")[0] ?? "amigo";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Buenos días" : hour < 19 ? "Buenas tardes" : "Buenas noches";

  return (
    <PrivateLayout>
      {/* perfil incompleto */}
      {profile === null && (
        <Reveal>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap",
            background: "var(--sand-3)", border: "1px solid #E8D2C1", borderRadius: 16, padding: "16px 20px", marginBottom: 24,
          }}>
            <div style={{ fontSize: 14.5, color: "var(--muted)" }}>
              <strong style={{ color: "var(--ink)" }}>¡Completa tu perfil!</strong> Cuéntanos un poco sobre ti para personalizar tu experiencia.
            </div>
            <Link to="/perfilado" className="btn btn-primary btn--sm">Completar →</Link>
          </div>
        </Reveal>
      )}

      {/* topbar */}
      <Reveal>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
              <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 30, letterSpacing: "-0.02em", margin: 0 }}>
                {greeting}, {firstName} 👋
              </h1>
              {planTier && (
                <span className="badge" style={{ background: PLAN_BADGE[planTier].bg, color: PLAN_BADGE[planTier].color }}>
                  Plan {PLAN_BADGE[planTier].label}
                </span>
              )}
            </div>
            <p style={{ fontSize: 15, color: "var(--muted-2)", margin: "0 0 10px" }}>¿Cómo te sientes hoy? Tómate tu tiempo.</p>
            {planTier && NEXT_TIER[planTier] && (
              <Link to={`/checkout?plan=${NEXT_TIER[planTier]}`} className="btn btn-outline btn--sm" style={{ textDecoration: "none" }}>
                Mejorar plan
              </Link>
            )}
          </div>
          <div className="hide-mobile" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid var(--border-3)", borderRadius: 999, padding: "10px 16px", color: "var(--muted-3)", fontSize: 14, width: 220 }}>
              🔍 Buscar guías, temas…
            </div>
            <div style={{ width: 42, height: 42, borderRadius: "50%", background: "#fff", border: "1px solid var(--border-3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, position: "relative" }}>
              🔔<span style={{ position: "absolute", top: 9, right: 10, width: 8, height: 8, borderRadius: "50%", background: "var(--copper)", border: "1.5px solid #fff" }} />
            </div>
          </div>
        </div>
      </Reveal>

      {/* continue reading hero */}
      <Reveal delay={1}>
        <div style={{
          background: "linear-gradient(135deg,#33302B,#413A31)", borderRadius: 24, padding: 32,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 30, marginBottom: 24,
          position: "relative", overflow: "hidden", flexWrap: "wrap",
        }}>
          <div style={{ position: "absolute", right: -30, top: -40, width: 200, height: 200, background: "radial-gradient(circle,var(--sage),transparent 70%)", opacity: 0.22 }} />
          <div style={{ position: "relative" }}>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#C2D6C5", marginBottom: 10 }}>
              Tu espacio te espera
            </div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 26, color: "var(--on-dark)", marginBottom: 8 }}>
              {profile ? `Sigue cuidando: ${profile.interes}` : "Empieza tu primer recurso"}
            </div>
            <div style={{ fontSize: 15, color: "var(--on-dark-muted)", marginBottom: 18 }}>
              Tu biblioteca y la comunidad están listas para acompañarte.
            </div>
            <Link to="/biblioteca" className="btn btn-dark btn--sm">Ir a mi biblioteca</Link>
          </div>
          <div className="hide-mobile" style={{
            position: "relative", width: 120, height: 150, borderRadius: 12,
            background: "repeating-linear-gradient(135deg,#4a4339,#4a4339 9px,#544B40 9px,#544B40 18px)",
            border: "1px solid rgba(250,245,238,.16)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <img src="/logo-psicodosis.png" style={{ width: 46, height: 46, objectFit: "contain", opacity: 0.85 }} />
          </div>
        </div>
      </Reveal>

      {/* stats row */}
      <div className="grid-3" style={{ marginBottom: 28 }}>
        {[
          { value: totalRes ?? "–", label: "guías en tu biblioteca", color: "var(--copper)" },
          { value: stats ? stats.ejercicios_completados : "–", label: "ejercicios completados", color: "var(--sage)" },
          { value: stats ? stats.aportes_comunidad : "–", label: "aportes en la comunidad", color: "#C99A6E" },
        ].map((s, i) => (
          <Reveal key={s.label} delay={(i % 4) as 0 | 1 | 2 | 3}>
            <div className="card" style={{ padding: 22 }}>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 30, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 14, color: "var(--muted)", marginTop: 2 }}>{s.label}</div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* quick access */}
      <Reveal><h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, letterSpacing: "-0.02em", margin: "0 0 16px" }}>Accesos rápidos</h2></Reveal>
      <div className="grid-3" style={{ marginBottom: 32 }}>
        {[
          { to: "/biblioteca", emoji: "📚", bg: "#EDDFCB", title: "Mi biblioteca", text: totalRes !== null ? `${totalRes} recurso${totalRes !== 1 ? "s" : ""} disponibles` : "Tus guías y material de apoyo." },
          { to: "/comunidad", emoji: "💬", bg: "#D9E4DA", title: "Comunidad", text: totalTemas !== null ? `${totalTemas} tema${totalTemas !== 1 ? "s" : ""} activos` : "Conversaciones por categoría." },
          { to: "/perfil", emoji: "👤", bg: "#EDDFCB", title: "Mi perfil", text: "Edita tus datos y preferencias." },
        ].map((a, i) => (
          <Reveal key={a.to} delay={(i % 4) as 0 | 1 | 2 | 3}>
            <Link to={a.to} style={{ textDecoration: "none", color: "inherit", display: "block", height: "100%" }}>
              <div className="card card--hover" style={{ padding: 24, height: "100%" }}>
                <div className="icon-pop" style={{ width: 48, height: 48, borderRadius: 14, background: a.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23, marginBottom: 16 }}>
                  {a.emoji}
                </div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17, marginBottom: 5 }}>{a.title}</div>
                <div style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.45 }}>{a.text}</div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>

      {/* recent community */}
      <Reveal>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 16 }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, letterSpacing: "-0.02em", margin: 0 }}>En la comunidad hoy</h2>
          <Link to="/comunidad" style={{ fontSize: 14, color: "var(--copper)", fontWeight: 700, textDecoration: "none" }}>Ver todo</Link>
        </div>
      </Reveal>
      <Reveal delay={1}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
          {temas.length === 0 ? (
            <div className="card" style={{ padding: "22px 20px", textAlign: "center", color: "var(--muted-2)", fontSize: 14.5 }}>
              Aún no hay temas activos en la comunidad.{" "}
              <Link to="/comunidad" style={{ color: "var(--copper)", fontWeight: 700, textDecoration: "none" }}>
                ¡Sé el primero en participar!
              </Link>
            </div>
          ) : (
            [...temas]
              .sort((a, b) => b.post_count - a.post_count)
              .slice(0, 2)
              .map((tema) => {
                const cat = getCategory(tema.categoria);
                return (
                  <Link key={tema.id} to={`/comunidad/${tema.id}`} className="card card--hover" style={{ padding: "18px 20px", display: "flex", alignItems: "center", gap: 16, textDecoration: "none", color: "inherit" }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#C99A6E,#E2D2BB)", flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tema.titulo}</div>
                      <div style={{ fontSize: 13, color: "var(--muted-2)" }}>
                        <span style={{ color: cat.text, fontWeight: 700 }}>{cat.label}</span> · {tema.post_count} respuesta{tema.post_count !== 1 ? "s" : ""} · {relTime(tema.ultima_actividad)}
                      </div>
                    </div>
                  </Link>
                );
              })
          )}
        </div>
      </Reveal>

      {/* topics */}
      <Reveal><h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, letterSpacing: "-0.02em", margin: "0 0 16px" }}>Explorar foros</h2></Reveal>
      <Reveal delay={1}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {TOPIC_LINKS.map((t) => (
            <Link key={t.label} to="/comunidad" className="chip hover-lift" style={{ background: t.color, border: "none" }}>
              <span>{t.emoji}</span>
              <span>{t.label}</span>
            </Link>
          ))}
        </div>
      </Reveal>
    </PrivateLayout>
  );
}
